import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Check } from 'lucide-react';
import { Button } from '../common/Button';

export interface GroupLayoutChoice {
  /** Group sizes for this layout, largest-first — e.g. [4, 4] or [3, 3, 2]. */
  sizes: number[];
  title: string;
  detail: string;
}

interface GroupLayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  /** Layouts to choose from. The first is preselected. */
  choices: GroupLayoutChoice[];
  onConfirm: (sizes: number[]) => void;
}

/**
 * Asks the admin how to split the duplas into groups when more than one sensible
 * layout exists (today only 8 duplas: two groups of 4, or three groups of 3+3+2).
 * Picking a layout and confirming runs the group draw — which also closes
 * registration, so the same warning as the inline button is shown here.
 */
export function GroupLayoutModal({
  isOpen,
  onClose,
  isLoading,
  choices,
  onConfirm,
}: GroupLayoutModalProps) {
  const [selected, setSelected] = useState(0);

  // Reset to the first option whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) setSelected(0);
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && choices.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm glass-card rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-1 gap-2">
              <h3 className="font-display text-lg font-bold text-white">Sortear grupos</h3>
              <button
                onClick={onClose}
                className="p-2 -mr-1 -mt-1 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors shrink-0"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-white/50 mb-5">
              Como você prefere dividir as duplas em grupos?
            </p>

            <div className="space-y-2">
              {choices.map((choice, i) => {
                const isSelected = i === selected;
                return (
                  <button
                    key={choice.sizes.join('-')}
                    onClick={() => setSelected(i)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-colors ${
                      isSelected
                        ? 'bg-turquoise/20 border-turquoise/50'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <span
                      className={`shrink-0 flex items-center justify-center w-9 h-9 rounded-lg ${
                        isSelected ? 'bg-turquoise/30 text-turquoise' : 'bg-white/10 text-white/50'
                      }`}
                    >
                      <Users className="w-4 h-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span
                        className={`block text-sm font-semibold ${
                          isSelected ? 'text-turquoise' : 'text-white'
                        }`}
                      >
                        {choice.title}
                      </span>
                      <span className="block text-[11px] text-white/40">{choice.detail}</span>
                    </span>
                    {isSelected && <Check className="w-4 h-4 text-turquoise shrink-0" />}
                  </button>
                );
              })}
            </div>

            <Button
              onClick={() => onConfirm(choices[selected].sizes)}
              fullWidth
              isLoading={isLoading}
              className="mt-5"
            >
              Sortear grupos
            </Button>
            <p className="text-[11px] text-white/40 text-center mt-2">
              Ao sortear os grupos, as inscrições serão encerradas.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
